"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import { Activity, AlertCircle, Calendar, FileText, Loader2, Pill, Search, Stethoscope, X } from "lucide-react";

import { searchPatientTimeline } from "@/app/actions/algolia-search-actions";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getPatientTimelineIndex } from "@/lib/config/algolia-config";
import { cn } from "@/lib/utils";
import { sanitizeHighlight } from "@/lib/utils/sanitize";

interface TimelineSearchResult {
  objectID: string;
  date: string;
  time?: string;
  type: string;
  title: string;
  description?: string;
  provider?: string;
  status?: string;
  specialty?: string;
  _highlightResult?: {
    title?: { value: string };
    description?: { value: string };
  };
}

interface AlgoliaTimelineSearchProps {
  /** REQUIRED: Patient ID - determines which patient-specific index to search */
  patientId: string;
  onResultSelect?: (result: TimelineSearchResult) => void;
  onSearchResults?: (results: TimelineSearchResult[], query: string) => void;
  placeholder?: string;
  className?: string;
}

const typeIcons: Record<string, React.ElementType> = {
  encounter: Stethoscope,
  medication: Pill,
  condition: AlertCircle,
  lab: Activity,
  imaging: FileText,
  procedure: Activity,
  default: FileText,
};

const typeColors: Record<string, string> = {
  encounter: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  medication: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  condition: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  lab: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  imaging: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  procedure: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400",
  default: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400",
};

/**
 * Algolia Timeline Search component
 * Uses patient-specific indices for direct search without filtering
 * Index name: patient_timeline_<patientId>
 */
export function AlgoliaTimelineSearch({
  patientId,
  onResultSelect,
  onSearchResults,
  placeholder = "Search timeline with Algolia...",
  className,
}: AlgoliaTimelineSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TimelineSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [indexName, setIndexName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Update index name when patientId changes
  useEffect(() => {
    setIndexName(getPatientTimelineIndex(patientId));
  }, [patientId]);

  // Debounced search - hits patient-specific index directly
  const searchTimeline = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setIsOpen(false);
        onSearchResults?.([], "");
        return;
      }

      setIsSearching(true);

      try {
        // Use server action instead of fetch
        const result = await searchPatientTimeline({
          query: searchQuery,
          patientId, // Required for patient-specific index lookup
          limit: 50,
        });

        if (result.success && result.hits) {
          setResults(result.hits as TimelineSearchResult[]);
          setIsOpen(result.hits.length > 0);
          onSearchResults?.(result.hits as TimelineSearchResult[], searchQuery);
        } else {
          setResults([]);
          setIsOpen(true); // Keep open to show "no results" message
        }
      } catch (_error) {
        setResults([]);
        setIsOpen(true); // Keep open to show error state
      } finally {
        setIsSearching(false);
      }
    },
    [patientId, onSearchResults]
  );

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      searchTimeline(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, searchTimeline]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleResultSelect(results[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleResultSelect = (result: TimelineSearchResult) => {
    onResultSelect?.(result);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const clearSearch = () => {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    onSearchResults?.([], "");
    inputRef.current?.focus();
  };

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const getTypeIcon = (type: string) => {
    const IconComponent = typeIcons[type.toLowerCase()] || typeIcons.default;
    return IconComponent;
  };

  const getTypeColor = (type: string) => {
    return typeColors[type.toLowerCase()] || typeColors.default;
  };

  const renderHighlightedText = (text: string | undefined, highlight?: { value: string }) => {
    if (highlight?.value) {
      return <span dangerouslySetInnerHTML={{ __html: sanitizeHighlight(highlight.value) }} />;
    }
    return text || "";
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder={placeholder}
          className="pl-9 pr-9"
        />
        {isSearching && (
          <Loader2 className="absolute right-9 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
        {query && !isSearching && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2"
            onClick={clearSearch}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full z-50 mt-1 w-full rounded-md border bg-popover shadow-lg">
          <ScrollArea className="max-h-[400px]">
            <div className="p-1">
              {results.map((result, index) => {
                const IconComponent = getTypeIcon(result.type);
                return (
                  <div
                    key={result.objectID}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-md p-3 transition-colors",
                      selectedIndex === index ? "bg-accent" : "hover:bg-accent/50"
                    )}
                    onClick={() => handleResultSelect(result)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
                        getTypeColor(result.type)
                      )}
                    >
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="flex-1 space-y-1 overflow-hidden">
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-medium [&_mark]:bg-yellow-200 [&_mark]:text-yellow-900 dark:[&_mark]:bg-yellow-500/30 dark:[&_mark]:text-yellow-200">
                          {renderHighlightedText(result.title, result._highlightResult?.title)}
                        </p>
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {result.type}
                        </Badge>
                      </div>
                      {result.description && (
                        <p className="truncate text-xs text-muted-foreground [&_mark]:bg-yellow-200 [&_mark]:text-yellow-900 dark:[&_mark]:bg-yellow-500/30 dark:[&_mark]:text-yellow-200">
                          {renderHighlightedText(result.description, result._highlightResult?.description)}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(result.date)}</span>
                        {result.provider && (
                          <>
                            <span>•</span>
                            <span>{result.provider}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
          <div className="border-t px-3 py-2 text-xs text-muted-foreground">
            {results.length} result{results.length !== 1 ? "s" : ""} in {indexName} • Powered by Algolia
          </div>
        </div>
      )}

      {/* No results */}
      {isOpen && query && results.length === 0 && !isSearching && (
        <div className="absolute top-full z-50 mt-1 w-full rounded-md border bg-popover p-4 text-center shadow-lg">
          <p className="text-sm text-muted-foreground">No timeline events found for "{query}"</p>
          <p className="mt-2 text-xs text-muted-foreground">
            Make sure to sync patient data to Algolia first using the "Sync to Algolia" button.
          </p>
          <p className="mt-1 text-xs text-muted-foreground/60">Index: {indexName}</p>
        </div>
      )}
    </div>
  );
}
